import { trpc } from "@/trpc";

/**
 * Gallery photos live in a private bucket behind signed URLs that last a day.
 * The stale time sits well under that expiry but is deliberately long: every
 * refetch mints NEW signed URLs, which busts the browser's image cache and
 * costs B2 egress for every thumbnail again. Refetch-on-focus still rescues a
 * tab left open past expiry.
 */
const GALLERY_STALE_TIME_MS = 12 * 60 * 60 * 1000;

/**
 * The gallery index: every gallery with at least one approved photo. Open to
 * any signed-in user. Covers are signed URLs, so the same expiry rationale
 * applies: stale time under the ~15 minute link lifetime, refetch on focus so
 * a tab left open re-signs its covers.
 */
export function useGalleriesList() {
  return trpc.members.gallery.list.useQuery(undefined, {
    staleTime: GALLERY_STALE_TIME_MS,
    refetchOnWindowFocus: true,
  });
}

/** The approved photos in an event's gallery. Open to any signed-in user. */
export function useEventGallery(eventId: string, enabled = true) {
  return trpc.members.gallery.getByEvent.useQuery(
    { event_id: eventId },
    {
      enabled,
      staleTime: GALLERY_STALE_TIME_MS,
      refetchOnWindowFocus: true,
    },
  );
}

/**
 * One gallery's meta and a page of its approved photos, for the gallery page.
 * Same signed-URL expiry rationale as useEventGallery: stale time under the
 * ~15 minute link lifetime, refetch on focus so a tab left open re-signs its
 * URLs. While a new page loads, the previous page stays on screen instead of
 * dropping back to the skeleton — but only within the same gallery, so
 * navigating to another gallery never flashes the old one's photos.
 */
export function useGalleryDetail(galleryId: string, page: number, enabled = true) {
  return trpc.members.gallery.get.useQuery(
    { gallery_id: galleryId, page },
    {
      enabled,
      staleTime: GALLERY_STALE_TIME_MS,
      refetchOnWindowFocus: true,
      placeholderData: (previous) => (previous?.id === galleryId ? previous : undefined),
    },
  );
}

/**
 * The approved photos a user is tagged in, for their profile's Photos tab.
 * Signed URLs again, so the same stale-time and refetch-on-focus rationale.
 */
export function useProfileTaggedPhotos(username: string) {
  return trpc.members.profile.taggedPhotos.useQuery(
    { username },
    {
      staleTime: GALLERY_STALE_TIME_MS,
      refetchOnWindowFocus: true,
    },
  );
}
